import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/auth";
import { Download, Upload, Trash2, Edit3, Save, Loader2, Package, Check, X, Plus } from "lucide-react";

function AdminEditRow({ d, onSave, onDelete, onUpload }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: d.name, description: d.description || "", version: d.version || "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setBusy(true);
    try { await onSave(d.id, form); setEdit(false); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { await onUpload(d.id, f); } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <tr data-testid={`admin-driver-${d.slug}`} className="border-b border-zinc-800">
      <td className="py-2.5 pr-3">
        {edit ? (
          <input className="input-lhs !py-1 !text-xs w-full" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} />
        ) : (
          <>
            <div className="font-semibold text-sm">{d.name}</div>
            <div className="text-[10px] text-zinc-500">{d.slug}</div>
          </>
        )}
      </td>
      <td className="py-2.5 pr-3">
        {edit
          ? <input className="input-lhs !py-1 !text-xs w-full" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} />
          : <span className="text-xs text-zinc-400 line-clamp-2">{d.description || "—"}</span>}
      </td>
      <td className="py-2.5 pr-3">
        {edit
          ? <input className="input-lhs !py-1 !text-xs w-20" value={form.version}
              onChange={e => setForm({ ...form, version: e.target.value })} />
          : <span className="font-mono-tech text-xs">{d.version || "—"}</span>}
      </td>
      <td className="py-2.5 pr-3">
        <span className="text-[10px] font-mono-tech text-zinc-500">
          {d.filename ? `${(d.size_bytes / (1024 * 1024)).toFixed(1)} MB` : "⚠ aucun fichier"}
        </span>
        <input ref={fileRef} type="file" className="hidden" onChange={upload} />
        <button onClick={() => fileRef.current?.click()} className="ml-2 text-xs text-sky-400 hover:underline">
          {d.filename ? "Remplacer" : "Upload"}
        </button>
      </td>
      <td className="py-2.5 pr-3 text-right">
        <button onClick={() => onSave(d.id, { enabled: !d.enabled })}
          data-testid={`toggle-driver-${d.slug}`}
          className={`text-[10px] px-2 py-1 rounded-full border font-mono-tech ${
            d.enabled ? "text-emerald-400 border-emerald-500/40" : "text-zinc-500 border-zinc-700"
          }`}>
          {d.enabled ? "ACTIF" : "OFF"}
        </button>
      </td>
      <td className="py-2.5 text-right whitespace-nowrap">
        {edit ? (
          <>
            <button onClick={save} disabled={busy} className="text-emerald-400 mr-2" data-testid={`save-driver-${d.slug}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
            <button onClick={() => setEdit(false)} className="text-zinc-500"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <>
            <button onClick={() => setEdit(true)} className="text-zinc-400 hover:text-white mr-2" data-testid={`edit-driver-${d.slug}`}><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => onDelete(d.id)} className="text-red-400 hover:text-red-300" data-testid={`del-driver-${d.slug}`}><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </td>
    </tr>
  );
}

export default function Pilotes({ publicMode = false }) {
  const { user } = useAuth?.() || { user: null };
  const isAdmin = user?.role === "admin";
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", description: "", version: "" });
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/drivers${isAdmin ? "?all_items=true" : ""}`);
      setDrivers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const download = (d) => { window.location.href = `${API}/drivers/${d.id}/download`; };

  const patchDriver = async (id, body) => {
    await axios.patch(`${API}/drivers/${id}`, body);
    await load();
  };
  const uploadDriver = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    await axios.post(`${API}/drivers/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setMsg({ ok: true, text: "Fichier téléversé." });
    setTimeout(() => setMsg(null), 2500);
    await load();
  };
  const delDriver = async (id) => {
    if (!window.confirm("Supprimer ce pilote ?")) return;
    await axios.delete(`${API}/drivers/${id}`); await load();
  };
  const createDriver = async () => {
    if (!newForm.name.trim()) { setMsg({ ok: false, text: "Nom requis" }); return; }
    try {
      await axios.post(`${API}/drivers`, newForm);
      setNewForm({ name: "", description: "", version: "" });
      setAdding(false); await load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Erreur" });
    }
  };

  return (
    <div data-testid="pilotes-section">
      {!publicMode && (
        <div className="mb-4 text-sm text-zinc-400">
          Les pilotes téléchargés sur ce portail sont destinés à <b className="text-orange-400">LHSunlocker Desktop</b> qui communiquera localement avec votre téléphone/USB.
          Le serveur ne détecte pas le téléphone et n'exécute aucun test USB.
        </div>
      )}

      {isAdmin && !publicMode && (
        <div className="card-lhs p-4 mb-4" data-testid="admin-driver-add-card">
          {!adding ? (
            <button onClick={() => setAdding(true)} className="btn-orange text-xs rounded-lg px-4 py-2 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Ajouter un pilote
            </button>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
              <input placeholder="Nom (ex: Mediatek USB Driver)" className="input-lhs md:col-span-2"
                value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })}
                data-testid="new-driver-name" />
              <input placeholder="Description" className="input-lhs md:col-span-2"
                value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                data-testid="new-driver-desc" />
              <input placeholder="Version" className="input-lhs"
                value={newForm.version} onChange={e => setNewForm({ ...newForm, version: e.target.value })}
                data-testid="new-driver-version" />
              <div className="flex gap-1">
                <button onClick={createDriver} data-testid="create-driver-btn" className="btn-orange text-xs rounded-lg px-3 flex-1">Créer</button>
                <button onClick={() => setAdding(false)} className="btn-ghost text-xs rounded-lg px-2"><X className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          {msg && <div className={`mt-2 text-xs ${msg.ok ? "text-emerald-400" : "text-red-400"}`}>{msg.text}</div>}
        </div>
      )}

      {loading ? (
        <div className="text-center text-zinc-500 py-10">Chargement…</div>
      ) : isAdmin && !publicMode ? (
        <div className="card-lhs p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="py-2 pr-3 text-left">Pilote</th>
                <th className="py-2 pr-3 text-left">Description</th>
                <th className="py-2 pr-3 text-left">Version</th>
                <th className="py-2 pr-3 text-left">Fichier</th>
                <th className="py-2 pr-3 text-right">Statut</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-drivers-table">
              {drivers.map(d => (
                <AdminEditRow key={d.id} d={d} onSave={patchDriver} onDelete={delDriver} onUpload={uploadDriver} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="public-drivers-grid">
          {drivers.map(d => (
            <div key={d.id} data-testid={`driver-${d.slug}`} className="card-lhs p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5 text-orange-500" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-sm leading-tight">{d.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-tech mt-0.5">
                    v{d.version || "?"} · MàJ {d.updated_at && new Date(d.updated_at).toLocaleDateString("fr-FR")}
                  </div>
                </div>
              </div>
              <div className="text-xs text-zinc-400 line-clamp-3 min-h-[2.5rem]">{d.description || "—"}</div>
              <button
                onClick={() => download(d)}
                disabled={!d.has_file}
                data-testid={`download-driver-${d.slug}`}
                className={`w-full rounded-lg py-2 text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 ${
                  d.has_file ? "btn-orange" : "border border-zinc-700 text-zinc-500 cursor-not-allowed"
                }`}
              >
                <Download className="w-3.5 h-3.5" />
                {d.has_file ? "Télécharger" : "Bientôt disponible"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
